import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useOnboardingStore } from '@/store/onboardingStore'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import { useTranslation } from 'react-i18next'
import { UserCircle2, Clock, Globe, Phone, User, Settings, ArrowRight, Languages, Calendar, Smartphone } from 'lucide-react'
import { formatPhoneNumber, cleanPhoneNumber, isValidPhoneNumber } from '@flows.cash/core'
import { Card } from '@/components/ui/card'
import { useProfileStore as useCoreProfileStore } from '@flows.cash/core'

interface PersonalInfoStepProps {
  onNext: () => void
}

// Supported languages (matching i18n configuration)
const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸', nativeName: 'English' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸', nativeName: 'Español' },
  { code: 'fr', name: 'French', flag: '🇫🇷', nativeName: 'Français' },
  { code: 'de', name: 'German', flag: '🇩🇪', nativeName: 'Deutsch' },
  { code: 'tr', name: 'Turkish', flag: '🇹🇷', nativeName: 'Türkçe' },
  { code: 'ar', name: 'Arabic', flag: '🇸🇦', nativeName: 'العربية' }
]

// Date formats matching the settings configuration
const dateFormats = [
  { value: 'MMM d, yyyy', label: 'MMM d, yyyy (Dec 31, 2024)' },
  { value: 'MM/dd/yyyy', label: 'MM/dd/yyyy (12/31/2024)' },
  { value: 'dd/MM/yyyy', label: 'dd/MM/yyyy (31/12/2024)' },
  { value: 'yyyy-MM-dd', label: 'yyyy-MM-dd (2024-12-31)' },
  { value: 'dd.MM.yyyy', label: 'dd.MM.yyyy (31.12.2024)' },
  { value: 'yyyy/MM/dd', label: 'yyyy/MM/dd (2024/12/31)' },
  { value: 'MMMM d, yyyy', label: 'MMMM d, yyyy (December 31, 2024)' },
  { value: 'd MMMM yyyy', label: 'd MMMM yyyy (31 December 2024)' }
]

// Time formats matching the settings configuration
const timeFormats = [
  { value: 'hh:mm a', label: '12-hour (2:30 PM)' },
  { value: 'HH:mm', label: '24-hour (14:30)' }
]

const formSchema = z.object({
  fullName: z.string().min(2, 'Full name must be at least 2 characters'),
  displayName: z.string().min(2, 'Display name must be at least 2 characters'),
  phone: z.string().optional(),
  dateFormat: z.string(),
  timeFormat: z.string(),
  language: z.string(),
})

export function PersonalInfoStep({ onNext }: PersonalInfoStepProps) {
  const { updateUserInfo } = useOnboardingStore()
  const { updateSettings } = useCoreProfileStore()
  const { user } = useAuth()
  const { t, i18n } = useTranslation()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: '',
      displayName: '',
      phone: '',
      dateFormat: 'MMM d, yyyy',
      timeFormat: 'hh:mm a',
      language: i18n.language || 'en',
    },
  })

  const handleLanguageChange = async (language: string) => {
    await i18n.changeLanguage(language)
    form.setValue('language', language)
  }

  async function onSubmit(values: z.infer<typeof formSchema>) {
    if (!user?.id) {
      toast.error('You must be logged in to continue')
      return
    }

    try {
      await updateUserInfo(values, user.id)
      await updateSettings(user.id, {
        full_name: values.fullName,
        display_name: values.displayName,
        phone: cleanPhoneNumber(values.phone || ''),
        preferences: {
          language: values.language,
          currency: 'USD',
          theme: 'light'
        }
      })
      await i18n.changeLanguage(values.language)
      onNext()
    } catch (error) {
      console.error('Error updating user info:', error)
      toast.error(t('settings.error', 'Failed to save your information'))
    }
  }

  return (
    <div className="space-y-10">
      {/* Hero Section */}
      <div className="text-center space-y-8">
        <div className="relative mx-auto w-20 h-20 mb-6">
          <div className="absolute inset-0 bg-gradient-to-r from-secondary to-primary dark:from-primary-dark dark:to-secondary-dark rounded-3xl opacity-20 animate-pulse"></div>
          <div className="relative bg-gradient-to-r from-secondary to-primary dark:from-primary-dark dark:to-secondary-dark rounded-3xl p-4 shadow-xl">
            <UserCircle2 className="h-12 w-12 text-white" />
          </div>
        </div>
        
        <div className="space-y-4 max-w-2xl mx-auto">
          <h3 className="text-2xl font-bold bg-gradient-to-r from-primary to-secondary dark:from-primary-dark dark:to-secondary-dark bg-clip-text text-transparent">
            {t('settings.profile.personalInfo', 'Tell us about yourself')}
          </h3>
          <p className="text-base text-text-secondary dark:text-text-dark-secondary leading-relaxed">
            {t('settings.profile.description', 'Let\'s get started by setting up your profile and preferences.')}
          </p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          {/* Personal Information Card */}
          <Card className="p-8 bg-background-secondary/50 dark:bg-background-dark-secondary/50 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark rounded-2xl shadow-sm">
            <div className="space-y-8">
              <div className="flex items-center gap-4 pb-4 border-b border-border dark:border-border-dark">
                <div className="p-3 rounded-2xl bg-secondary/10 dark:bg-primary-dark/10 border border-secondary/20 dark:border-primary-dark/20">
                  <User className="h-6 w-6 text-secondary dark:text-primary-dark" />
                </div>
                <div className="space-y-1">
                  <h4 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
                    {t('settings.profile.basicInfo', 'Basic Information')}
                  </h4>
                  <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                    Tell us how you'd like to be identified
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <FormField
                  control={form.control}
                  name="fullName"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel className="text-text-primary dark:text-text-dark-primary font-medium text-sm">
                        {t('settings.profile.fullName', 'Full Name')}
                      </FormLabel>
                      <FormControl>
                        <Input 
                          placeholder={t('settings.profile.fullNamePlaceholder', 'Enter your full name')} 
                          {...field}
                          className="h-12 bg-background-input dark:bg-background-dark-input border border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark focus:ring-2 focus:ring-secondary/20 dark:focus:ring-primary-dark/20 rounded-xl transition-all duration-200 text-base" 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="displayName"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel className="text-text-primary dark:text-text-dark-primary font-medium text-base">
                        {t('settings.profile.displayName', 'Display Name')}
                      </FormLabel>
                      <FormControl>
                        <Input 
                          placeholder={t('settings.profile.displayNamePlaceholder', 'How should we call you?')} 
                          {...field}
                          className="h-12 bg-background-input dark:bg-background-dark-input border border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark focus:ring-2 focus:ring-secondary/20 dark:focus:ring-primary-dark/20 rounded-xl transition-all duration-200 text-base" 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem className="space-y-3">
                    <FormLabel className="text-text-primary dark:text-text-dark-primary font-medium text-base">
                      {t('settings.profile.phone', 'Phone Number')} 
                      <span className="text-text-secondary dark:text-text-dark-secondary text-sm font-normal ml-2">
                        (Optional)
                      </span>
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2">
                          <Smartphone className="h-5 w-5 text-text-secondary dark:text-text-dark-secondary" />
                        </div>
                        <Input 
                          placeholder={t('settings.profile.phonePlaceholder', 'Enter your phone number')} 
                          {...field}
                          onChange={(e) => {
                            const formatted = formatPhoneNumber(e.target.value)
                            field.onChange(formatted)
                          }}
                          className="h-12 pl-12 bg-background-input dark:bg-background-dark-input border border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark focus:ring-2 focus:ring-secondary/20 dark:focus:ring-primary-dark/20 rounded-xl transition-all duration-200 text-base" 
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </Card>

          {/* Preferences Card */}
          <Card className="p-8 bg-background-secondary/50 dark:bg-background-dark-secondary/50 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark rounded-2xl shadow-sm">
            <div className="space-y-8">
              <div className="flex items-center gap-4 pb-4 border-b border-border dark:border-border-dark">
                <div className="p-3 rounded-2xl bg-secondary/10 dark:bg-primary-dark/10 border border-secondary/20 dark:border-primary-dark/20">
                  <Settings className="h-6 w-6 text-secondary dark:text-primary-dark" />
                </div>
                <div className="space-y-1">
                  <h4 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary">
                    {t('settings.preferences.title', 'Preferences')}
                  </h4>
                  <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                    Customize your experience
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <FormField
                  control={form.control}
                  name="language"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel className="text-text-primary dark:text-text-dark-primary font-medium text-base flex items-center gap-2">
                        <Languages className="h-4 w-4" />
                        {t('settings.language.title', 'Language')}
                      </FormLabel>
                      <Select 
                        value={field.value} 
                        onValueChange={(value) => {
                          field.onChange(value)
                          handleLanguageChange(value)
                        }}
                      >
                        <FormControl>
                          <SelectTrigger className="h-12 bg-background-input dark:bg-background-dark-input border border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark rounded-xl text-base">
                            <SelectValue placeholder={t('settings.language.select', 'Select language')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="rounded-xl border border-border dark:border-border-dark bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150">
                          {languages.map((lang) => (
                            <SelectItem key={lang.code} value={lang.code} className="rounded-lg">
                              <div className="flex items-center gap-3">
                                <span className="text-lg">{lang.flag}</span>
                                <div className="flex flex-col">
                                  <span className="font-medium">{lang.name}</span>
                                  <span className="text-sm text-text-secondary dark:text-text-dark-secondary">{lang.nativeName}</span>
                                </div>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="dateFormat"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel className="text-text-primary dark:text-text-dark-primary font-medium text-base flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        {t('settings.dateFormat.title', 'Date Format')}
                      </FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger className="h-12 bg-background-input dark:bg-background-dark-input border border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark rounded-xl text-base">
                            <SelectValue placeholder={t('settings.dateFormat.select', 'Select date format')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="rounded-xl border border-border dark:border-border-dark bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150">
                          {dateFormats.map((format) => (
                            <SelectItem key={format.value} value={format.value} className="rounded-lg">
                              {format.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="timeFormat"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel className="text-text-primary dark:text-text-dark-primary font-medium text-base flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        {t('settings.timeFormat.title', 'Time Format')}
                      </FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger className="h-12 bg-background-input dark:bg-background-dark-input border border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark rounded-xl text-base">
                            <SelectValue placeholder={t('settings.timeFormat.select', 'Select time format')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="rounded-xl border border-border dark:border-border-dark bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150">
                          {timeFormats.map((format) => (
                            <SelectItem key={format.value} value={format.value} className="rounded-lg">
                              {format.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-center pt-4">
            <Button 
              type="submit" 
              disabled={form.formState.isSubmitting}
              className="bg-primary hover:bg-primary/90 dark:bg-primary-dark dark:hover:bg-primary-dark/90 text-white px-10 py-4 text-lg font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-3 h-14 min-w-[200px] active:scale-[0.98]"
            >
              {form.formState.isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t('common.saving', 'Saving...')}
                </>
              ) : (
                <>
                  {t('common.continue', 'Continue')}
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
} 